<script setup lang="ts">
/** AR-E-RORC (Corp) — Notice of Controllers, **법인(corporate) 지배자용** (S.386AH/386AI). 지배자 1인당 1부(상위 DocScope).
 *  개인용(RorcDoc)과 같은 키 체계 + 법인 항목(UEN·등록사무소·법적형태·관할/법령·등록부명/번호).
 *  서명: 법인의 대표자(controller_rep_name) — Designation 은 "Director / Secretary / Authorised representative of …" 중 기재.
 *  Yes/No 회신(is_still_controller · has_relevant_change)은 선택 시 문서에서 동그라미 표시([[circle:…]]). */
import { inject } from 'vue';
import CddVarField from '../../aura/agm/cdd/CddVarField.vue';
import AgmSigners from '../../aura/agm/cdd/AgmSigners.vue';
import { useAgmFill, DOC_SCOPE } from '../../aura/agm/cdd/useCddFill';
import '../../aura/agm/docs/agmDoc.css';

const fill = useAgmFill();
const scope = inject(DOC_SCOPE, '');
function yn(k: string) { const v = String(fill.values[scope + k] ?? '').trim().toLowerCase(); return v === 'yes' ? 'yes' : v === 'no' ? 'no' : ''; }
function setYn(k: string, v: 'yes' | 'no') { fill.setValue(scope + k, yn(k) === v ? '' : v); }
</script>

<template>
  <div class="agm-doc">
    <div class="doc-head" style="text-align:center">
      <div class="doc-company"><CddVarField field-key="company_name" label="Company Name" /></div>
      <div>Incorporated in the Republic of Singapore</div>
      <div>Unique Entity Number: <CddVarField field-key="company_uen" label="UEN" /></div>
      <div>(“the Company”)</div>
    </div>
    <div class="rorc-title">
      <div class="t1">NOTICE OF CONTROLLERS</div>
      <div class="t2">FORM OF NOTICE MENTIONED IN SECTION 386AH(1) AND 386AI(1) OF THE SINGAPORE COMPANIES ACT 1967</div>
      <div class="t2">TO BE COMPLETED BY EXISTING CONTROLLER</div>
    </div>

    <p class="doc-p">Date of notice: <CddVarField field-key="notice_date" label="Notice date" /></p>
    <p class="doc-p">Attention: <CddVarField field-key="controller_name" label="Corporate controller" /></p>
    <p class="doc-p">This notice is sent under section 386AH(1) and 386AI(1) of the Singapore Companies Act 1967 and requires you to provide the following information <b><u>within 30 days</u></b> after the date of this notice. Please send your reply to <b><CddVarField field-key="reply_address" label="Reply address" /></b>.</p>
    <p class="doc-p notice-red">Please kindly complete and furnish the information as required below.</p>
    <p class="doc-p"><i>Failure to provide the information required by this notice may be an offence.</i></p>
    <p class="doc-p">Yours sincerely,<br />Name: <CddVarField field-key="secretary_name" label="Secretary name" /><br />Designation: <CddVarField field-key="secretary_designation" label="Designation" /></p>

    <p class="sec-title">1. Are you still a registrable controller of the Company?</p>
    <p class="doc-p">Your reply:
      <span class="circ" :class="{ sel: yn('is_still_controller') === 'yes' }" @click="setYn('is_still_controller','yes')">Yes</span>
      /
      <span class="circ" :class="{ sel: yn('is_still_controller') === 'no' }" @click="setYn('is_still_controller','no')">No</span>
      <span class="yn-hint">(circle your reply)</span>. If No, date you ceased to be a registrable controller: <CddVarField field-key="cessation_date" label="Cessation date" />.</p>

    <p class="sec-title">2. Particulars per your last declaration made on <CddVarField field-key="last_declaration_date" label="Last declaration date" /></p>
    <table class="cdd-tbl reg-tbl">
      <tbody>
        <tr><td class="reg-l">(i) Full name</td><td><CddVarField field-key="controller_full_name" label="Full name" /></td></tr>
        <tr><td class="reg-l">(ii) Unique entity number issued by the Registrar, if any</td><td><CddVarField field-key="controller_uen" label="UEN" /></td></tr>
        <tr><td class="reg-l">(iii) Registered office address</td><td><CddVarField field-key="controller_registered_office" label="Registered office" /></td></tr>
        <tr><td class="reg-l">(iv) Legal form</td><td><CddVarField field-key="controller_legal_form" label="Legal form" /></td></tr>
        <tr><td class="reg-l">(v) Email address</td><td><CddVarField field-key="controller_email" label="Email" /></td></tr>
        <tr><td class="reg-l">(vi) Contactable phone / landline number</td><td><CddVarField field-key="controller_phone" label="Phone" /></td></tr>
        <tr><td class="reg-l">(vii) Jurisdiction where, and statute under which, formed or incorporated</td><td><CddVarField field-key="controller_jurisdiction_statute" label="Jurisdiction / statute" /></td></tr>
        <tr><td class="reg-l">(viii) Name of the corporate entity register, if applicable</td><td><CddVarField field-key="controller_register_name" label="Register name" /></td></tr>
        <tr><td class="reg-l">(ix) Identification / registration number on that register, if applicable</td><td><CddVarField field-key="controller_register_id" label="Register number" /></td></tr>
        <tr><td class="reg-l">(x) Date you became a corporate controller</td><td><CddVarField field-key="controller_became_date" label="Became date" /></td></tr>
      </tbody>
    </table>

    <p class="doc-p">Has a relevant change in your particulars set out above occurred? Your reply:
      <span class="circ" :class="{ sel: yn('has_relevant_change') === 'yes' }" @click="setYn('has_relevant_change','yes')">Yes</span>
      /
      <span class="circ" :class="{ sel: yn('has_relevant_change') === 'no' }" @click="setYn('has_relevant_change','no')">No</span>
      <span class="yn-hint">(circle your reply)</span></p>

    <p class="sec-title">3. If your reply is Yes, provide the following</p>
    <table class="cdd-tbl reg-tbl">
      <tbody>
        <tr><td class="reg-l">(a) Date of the change</td><td><CddVarField field-key="change_date" label="Change date" /></td></tr>
        <tr><td class="reg-l">(i) New full name</td><td><CddVarField field-key="new_full_name" label="New full name" /></td></tr>
        <tr><td class="reg-l">(ii) New unique entity number, if any</td><td><CddVarField field-key="new_uen" label="New UEN" /></td></tr>
        <tr><td class="reg-l">(iii) New registered office address</td><td><CddVarField field-key="new_registered_office" label="New registered office" /></td></tr>
        <tr><td class="reg-l">(iv) New legal form</td><td><CddVarField field-key="new_legal_form" label="New legal form" /></td></tr>
        <tr><td class="reg-l">(v) New email address</td><td><CddVarField field-key="new_email" label="New email" /></td></tr>
        <tr><td class="reg-l">(vi) New contactable phone / landline number</td><td><CddVarField field-key="new_phone" label="New phone" /></td></tr>
        <tr><td class="reg-l">(vii) New jurisdiction and statute</td><td><CddVarField field-key="new_jurisdiction_statute" label="New jurisdiction / statute" /></td></tr>
        <tr><td class="reg-l">(viii) New name of the corporate entity register, if applicable</td><td><CddVarField field-key="new_register_name" label="New register name" /></td></tr>
        <tr><td class="reg-l">(ix) New identification / registration number, if applicable</td><td><CddVarField field-key="new_register_id" label="New register number" /></td></tr>
        <tr><td class="reg-l">(x) New date you became a corporate controller</td><td><CddVarField field-key="new_became_date" label="New became date" /></td></tr>
      </tbody>
    </table>

    <p class="doc-p"><b>CONFIRMATION OF PARTICULARS BY REGISTERED CONTROLLER</b> — I/We confirm the particulars provided are true and correct, and will update the Company on any changes.</p>

    <div class="sign-block" style="margin-top:12px">
      <table class="cdd-tbl" style="width:auto">
        <tbody>
          <tr><td class="cdd-lcell">Name</td><td>:&nbsp; <CddVarField field-key="controller_rep_name" label="Authorised signatory" /></td></tr>
          <tr><td class="cdd-lcell">Designation</td><td>:&nbsp; <CddVarField field-key="controller_designation" label="Director / Secretary / Authorised representative of …" /></td></tr>
          <tr><td class="cdd-lcell">Shareholding</td><td>:&nbsp; <CddVarField field-key="controller_shareholding" label="%" /> %</td></tr>
          <tr><td class="cdd-lcell">Date</td><td>:&nbsp; <CddVarField field-key="declaration_date" label="Date" /></td></tr>
        </tbody>
      </table>
      <div style="margin-top:8px"><AgmSigners var-key="rorc_Signers" board-label="Authorised signatory" /></div>
    </div>
  </div>
</template>

<style scoped>
.rorc-title { text-align: center; margin: 10px 0 14px; }
.rorc-title .t1 { font-weight: 700; text-decoration: underline; }
.rorc-title .t2 { font-size: 11px; font-weight: 700; text-decoration: underline; margin-top: 4px; }
.notice-red { color: #d3382f; }
.doc-company { font-weight: 800; font-size: 13.5px; }
.circ { cursor: pointer; user-select: none; padding: 1px 9px; margin: 0 2px; display: inline-block; line-height: 1.5; }
.circ.sel { border: 1.6px solid #111; border-radius: 999px; font-weight: 700; }
.circ:hover { color: #1b64da; }
.yn-hint { font-size: 11px; color: #8a97b5; margin-left: 4px; }
.reg-tbl { width: 100%; border-collapse: collapse; margin: 4px 0; }
.reg-tbl td { border: 1px solid #d3dbe6; padding: 5px 8px; vertical-align: top; }
.reg-l { width: 46%; background: #f7f9fc; font-weight: 600; color: #42506a; }
</style>
